import CourseDelivery from "components/admin/content/CourseDelivery";
import Enrollments from "components/admin/content/Enrollments";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Compass, CreditCard, Inbox as InboxTab, Menu, PanelLeftClose, PanelLeftOpen, Search, Settings as SettingsIcon, Sun, X } from "lucide-react";
import { RouteTabs, useAppEffects, useRubberBand, useScrolledPast } from "components/ios/Effects";
import { useClaimThemeControl } from "components/ios/Appearance";
import { useConfirm } from "components/ios/Alert";
import { SettingsSheet } from "components/ios/SettingsSheet";
import { useWallpaper } from "components/ios/Wallpaper";
import CommandPalette from "components/admin/workspace/CommandPalette";
import Today from "components/admin/workspace/Today";
import Inbox from "components/admin/workspace/Inbox";
import { COUNT_CAPS, NavCount, TodayFeed, sectionCounts, useTodayFeed } from "components/admin/workspace/Counts";
import { SECTIONS, TAB_LABELS, pathOf, sectionOf } from "components/admin/workspace/sections";
import { signOutAdmin } from "lib/adminAuth";
import { hasCapability, useAdmin } from "components/admin/RequireAdmin";
import {
  ErrorNotice,
  Loading,
  Record,
  useRemote,
} from "components/admin/workspace/Controls";
import {
  MODULES,
  CMS,
  endpoint,
  splitView,
  titleOf,
} from "components/admin/workspace/catalog";
import { SplitView, useSplitClose } from "components/ios/SplitView";
import { Collection } from "components/admin/workspace/Records";
import { SyncLine, SyncRecords } from "components/admin/workspace/Sync";
import {
  CreateOperation,
  DomainActions,
  SettingsForm,
  canExport,
} from "components/admin/workspace/Actions";
import ContentEditor from "components/admin/content/ContentEditor";
import ContentLibrary from "components/admin/content/ContentLibrary";
import Editor from "components/admin/workspace/Editor";
import Upload from "components/admin/workspace/Upload";
import PaperworkManagement from "components/admin/paperwork/PaperworkManagement";
import LinkCatchUp from "components/admin/workspace/LinkCatchUp";
import Orders from "components/admin/commerce/Orders";
import Partners from "components/admin/partners/Partners";
import BankInstructions from "components/admin/commerce/BankInstructions";
import { LegalTexts, OwnerSettings, StripeConnection, StripeStatusLine } from "components/admin/settings/ShopSettings";
import PaperworkPrices from "components/admin/settings/PaperworkPrices";
import "./AdminWorkspace.css";
const SIDEBAR_KEY = "magellan-admin-sidebar";
export default function AdminWorkspace() {
  const profile = useAdmin(),
    location = useLocation(),
    nav = useNavigate();
  const [menu, setMenu] = useState(false),
    [palette, setPalette] = useState(false),
    [settings, setSettings] = useState(false),
    [error, setError] = useState(null);
  // Desktop menu folds to an icon rail (remembered on this device); Cmd/Ctrl+\ toggles it.
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem(SIDEBAR_KEY) === "collapsed"; } catch { return false; } });
  const toggleSidebar = useCallback(() => setCollapsed((was) => {
    try { localStorage.setItem(SIDEBAR_KEY, was ? "open" : "collapsed"); } catch { /* This visit only. */ }
    return !was;
  }), []);
  const [confirmDialog, ask] = useConfirm();
  const wallpaper = useWallpaper({ staff: true });
  const heading = useRef(null),
    titleBlock = useRef(null),
    content = useRef(null),
    lastPath = useRef("");
  const { effects, setEffects } = useAppEffects();
  useClaimThemeControl();
  useRubberBand(content);
  const scrolled = useScrolledPast(titleBlock);
  const parts = location.pathname.split("/").filter(Boolean);
  const domain =
    parts[1] === "resources" && parts[3] === "edit"
      ? "legacy-resources"
      : parts[1] === "webinars"
        ? "courses"
        : parts[1] || "overview";
  const id = parts[2];
  const module = MODULES.find((m) => m[0] === domain);
  const permitted = (cap) =>
    !cap ||
    (cap === "exports" ? canExport(profile) : hasCapability(profile, cap));
  const visible = MODULES.filter((m) => permitted(m[3]));
  const visibleKeys = new Set(visible.map((m) => m[0]));
  const labelOf = (key) => MODULES.find((m) => m[0] === key)?.[1] || key;
  const section = sectionOf(domain);
  const sections = SECTIONS.map((s) => ({ ...s, items: s.items.filter((k) => visibleKeys.has(k)) })).filter((s) => s.items.length);
  const subpages = section ? section.items.filter((k) => visibleKeys.has(k)) : [];
  const title = module?.[1] || "Page not found";
  // Live menu counts from /admin/today, shared with the Today screen.
  const feed = useTodayFeed(COUNT_CAPS.some((cap) => hasCapability(profile, cap)));
  const counts = sectionCounts(feed?.data);
  useEffect(() => {
    setMenu(false);
    // Picking a lead inside the Inbox, or a row in a split view, keeps focus with the list and card
    // (SplitView moves it); every other move focuses the page title.
    const inbox = (path) => /^\/admin\/submissions(\/|$)/.test(path);
    const split = (path) => {
      const [, , d, sub, edit] = path.split("/");
      const resolved = d === "resources" && edit === "edit" ? "legacy-resources" : d === "webinars" ? "courses" : d;
      return sub !== "new" && splitView(resolved) ? resolved : null;
    };
    const stay = (inbox(location.pathname) && inbox(lastPath.current)) || (split(location.pathname) && split(location.pathname) === split(lastPath.current));
    if (!stay) heading.current?.focus();
    lastPath.current = location.pathname;
  }, [location.pathname]);
  useEffect(() => {
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName) || e.target?.isContentEditable;
      if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || (e.key === "/" && !typing)) {
        e.preventDefault();
        setPalette(true);
      }
      if (e.key === "\\" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggleSidebar();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleSidebar]);
  const signOut = async () => {
    const sure = await ask({ title: "Sign out?", message: "You will need to sign in again to use Magellan admin.", confirm: "Sign out", destructive: true });
    if (!sure) return;
    try {
      await signOutAdmin();
      nav("/admin/login", { replace: true });
    } catch (e) {
      setError(e);
    }
  };
  const name = profile.displayName || profile.email || "Staff account";
  const initials = name.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
  return (
    <TodayFeed.Provider value={feed}>
      <div className={`admin-workspace ios-app ${menu ? "aw-menu-open" : ""} ${collapsed ? "aw-collapsed" : ""}`} style={wallpaper.style}>
        <a href="#admin-main" className="aw-skip">
          Skip to workspace
        </a>
        <aside id="admin-sidebar" className={`aw-sidebar ios-material ${menu ? "aw-open" : ""}`}>
          <div className="aw-side-head">
            <Link className="aw-brand" to="/admin">
              <span className="aw-brand-mark"><Compass size={20} strokeWidth={1.75} /></span>
              <span>MAGELLAN<small>ADMIN</small></span>
            </Link>
            <button className="ios-btn ios-btn-gray ios-icon-btn aw-side-close" aria-label="Close navigation" onClick={() => setMenu(false)}>
              <X size={18} />
            </button>
            <button className="ios-btn ios-btn-plain ios-icon-btn aw-side-toggle" aria-label={collapsed ? "Expand menu" : "Collapse menu"}
              title={`${collapsed ? "Expand" : "Collapse"} menu (${navigator.platform?.includes("Mac") ? "⌘" : "Ctrl+"}\\)`} aria-expanded={!collapsed} aria-controls="admin-sidebar" onClick={toggleSidebar}>
              {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>
          </div>
          <button type="button" className="ios-search aw-side-search" title="Search" onClick={() => setPalette(true)}>
            <Search size={16} aria-hidden="true" /> Search <kbd>⌘K</kbd>
          </button>
          <nav aria-label="Admin navigation" className="aw-nav">
            {sections.map((s) => {
              const active = section?.key === s.key;
              const Icon = s.icon;
              return (
                <NavLink key={s.key} end={s.key === "today"} to={pathOf(s.items[0])}
                  className={`aw-nav-item ios-sidebar-item ${active ? "active" : ""} ${s.folded ? "aw-nav-quiet" : ""}`}
                  aria-current={active ? "page" : undefined} title={collapsed ? s.label : undefined}>
                  <span className="ios-tile" data-tint={s.tint}><Icon aria-hidden="true" /></span>
                  <span className="aw-nav-label">{s.label}</span>
                  <NavCount value={counts[s.key]} />
                </NavLink>
              );
            })}
          </nav>
          <div className="aw-identity ios-card">
            <div className="aw-identity-row">
              <span className="aw-avatar" aria-hidden="true">{initials || "M"}</span>
              <span className="aw-identity-text">
                <strong>{name}</strong>
                <span>{profile.role}</span>
              </span>
              <button className="ios-btn ios-btn-gray ios-icon-btn aw-settings-btn" aria-label="Settings" title="Settings: appearance, wallpaper, sign out" aria-haspopup="dialog" onClick={() => setSettings(true)}>
                <SettingsIcon size={17} />
              </button>
            </div>
          </div>
        </aside>
        {menu && <button className="aw-side-scrim" aria-label="Close navigation" onClick={() => setMenu(false)} />}
        <main className="aw-main" id="admin-main">
          <header className="ios-toolbar aw-toolbar" data-scrolled={scrolled}>
            <button
              className="ios-btn ios-btn-gray ios-icon-btn aw-menu-btn"
              aria-label={menu ? "Close navigation" : "Open navigation"}
              aria-expanded={menu}
              aria-controls="admin-sidebar"
              onClick={() => setMenu(!menu)}
            >
              <Menu size={18} />
            </button>
            <span className="ios-toolbar-title" aria-hidden="true">{title}</span>
            <div className="aw-toolbar-actions">
              <button type="button" className="ios-search aw-toolbar-search" data-glass="bar" onClick={() => setPalette(true)}>
                <Search size={16} aria-hidden="true" /> <span>Search or paste a MAG reference</span> <kbd>⌘K</kbd>
              </button>
              <Link to="/" className="ios-btn ios-btn-gray aw-view-site" data-glass="capsule">
                View website <ArrowUpRight size={15} aria-hidden="true" />
              </Link>
            </div>
          </header>
          <div className="ios-rubber" ref={content}>
            <div className="aw-page-heading" ref={titleBlock}>
              <h1 className="ios-largetitle" ref={heading} tabIndex={-1}>
                {subpages.length > 1 ? section.label : title}
              </h1>
              {domain === "overview" && (
                <p className="ios-subhead">{new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</p>
              )}
            </div>
            {subpages.length > 1 && (
              <RouteTabs
                label={`${section.label} pages`}
                className="aw-tabs"
                items={subpages.map((k) => ({
                  to: pathOf(k),
                  label: TAB_LABELS[k] || labelOf(k),
                  active: k === domain || (domain === "course-delivery" && k === "course-library"),
                  badge: <NavCount value={{ submissions: counts.leads, orders: counts.sales, support: counts.customers, sync: counts.system }[k]} className="aw-tab-count" />,
                }))}
              />
            )}
            <ErrorNotice error={error} />
            {!module ? (
              <p className="aw-notice">
                This Admin page does not exist.{" "}
                <Link to="/admin">Return to Today</Link>
              </p>
            ) : !permitted(module[3]) ? (
              <p className="aw-notice" role="alert">
                You do not have permission to view this area.
              </p>
            ) : domain === "overview" ? (
              <Today />
            ) : domain === "submissions" ? (
              <Inbox id={id} />
            ) : domain === "paperwork" ? (
              <PaperworkManagement key={id || "list"} id={id} />
            ) : domain === "course-enrollments" ? (
              <Enrollments key={id || "list"} id={id} />
            ) : domain === "course-delivery" ? (
              id ? <CourseDelivery key={id} id={id} /> : <p>Select a course in <Link to="/admin/course-library">Course library</Link> to manage delivery.</p>
            ) : domain === "partners" ? (
              <Partners id={id} />
            ) : domain === "orders" ? (
              <>
                {!id && hasCapability(profile, "payments.status.read") && <StripeStatusLine />}
                <Orders id={id} />
              </>
            ) : domain === "sync" && !id ? (
              <><LinkCatchUp /><SyncRecords /></>
            ) : (
              <WorkspaceModule
                key={splitView(domain) && id !== "new" ? domain : `${domain}:${id || ""}`}
                domain={domain}
                id={id}
              />
            )}
            <footer className="aw-footer">
              Magellan International · Website administration
            </footer>
          </div>
        </main>
        <nav className="ios-tabbar" aria-label="Quick navigation" data-glass="bar">
          {[["overview", "Today", Sun, null], ["submissions", "Inbox", InboxTab, "leads"], ["orders", "Orders", CreditCard, "sales"]]
            .filter(([k]) => visibleKeys.has(k))
            .map(([k, label, Icon, count]) => (
              <NavLink key={k} end={k === "overview"} to={pathOf(k)}>
                <Icon size={22} aria-hidden="true" />
                {count && <NavCount value={counts[count]} className="aw-tab-count" />}
                {label}
              </NavLink>
            ))}
          <a href="#search" onClick={(e) => { e.preventDefault(); setPalette(true); }}>
            <Search size={22} aria-hidden="true" />
            Search
          </a>
        </nav>
        <CommandPalette open={palette} onClose={() => setPalette(false)} modules={visible} />
        <SettingsSheet open={settings} onClose={() => setSettings(false)} effects={effects} setEffects={setEffects} wallpaper={wallpaper}
          account={{ name, detail: `${profile.email || ""}${profile.email ? " · " : ""}${profile.role || ""}` }} onSignOut={signOut} />
        {confirmDialog}
      </div>
    </TodayFeed.Provider>
  );
}
function WorkspaceModule({ domain, id }) {
  const profile = useAdmin(),
    [tick, setTick] = useState(0),
    [receipt, setReceipt] = useState(null);
  const split = splitView(domain);
  const close = useSplitClose(`/admin/${domain}`);
  // The list beside the card has its own counter: its Refresh must not reload (and wipe) the card.
  const [listTick, setListTick] = useState(0);
  // In a split view the module stays mounted between records; a receipt belongs to one record.
  useEffect(() => { setReceipt(null); }, [id]);
  const refresh = (result) => {
    if (result?.requestId) setReceipt(result.requestId);
    setTick((n) => n + 1);
    setListTick((n) => n + 1);
  };
  const state = useRemote(
    id && id !== "new" ? `${endpoint(domain)}/${encodeURIComponent(id)}` : null,
    tick,
    { keep: CMS.includes(domain) },
  );
  const canCreate = CMS.includes(domain)
    ? !["media", "legacy-resources"].includes(domain) &&
      hasCapability(profile, "content.edit")
    : domain === "resources"
      ? hasCapability(profile, "resources.manage")
      : domain === "grants"
        ? hasCapability(profile, "grants.manage")
        : domain === "team" || domain === "invitations"
          ? hasCapability(profile, "team.manage")
          : domain === "support"
            ? hasCapability(profile, "support.manage")
            : domain === "exports"
              ? hasCapability(profile, "crm.export") ||
                hasCapability(profile, "recruiting.export")
              : false;
  const ContentForm = ["speakers", "course-library"].includes(domain) ? ContentEditor : Editor;
  if (!id && ["speakers", "course-library"].includes(domain)) return <ContentLibrary domain={domain} tick={tick} onRefresh={refresh} />;
  if (id === "new")
    return (
      <>
        <Link to={`/admin/${domain}`} className="aw-link">
          ← Back to records
        </Link>
        {!canCreate ? (
          <p role="alert">
            This creation action is not available to your account.
          </p>
        ) : CMS.includes(domain) || domain === "resources" ? (
          <ContentForm domain={domain} onRefresh={refresh} />
        ) : (
          <CreateOperation domain={domain} />
        )}
      </>
    );
  const detail = id ? (
      <>
        {receipt && (
          <div className="aw-notice" role="status">
            Changes saved. Receipt: {receipt}
          </div>
        )}
        <div className="aw-row">
          <Link to={`/admin/${domain}`} className="aw-link aw-back">
            ← Back to records
          </Link>
          <button className="aw-button" onClick={refresh}>
            Reload latest record
          </button>
        </div>
        <Loading state={state}>
          {state.data && (
            <>
              {!["speakers", "course-library"].includes(domain) && <section className="aw-panel">
                <h2>{titleOf(state.data)}</h2>
                <Record data={state.data} />
                {["submissions", "applications"].includes(domain) &&
                  hasCapability(profile, "integrations.read") && (
                    <SyncLine
                      type={domain === "submissions" ? "submission" : "application"}
                      id={id}
                    />
                  )}
              </section>}
              {CMS.includes(domain) || domain === "resources" ? (
                <ContentForm
                  key={`${domain}:${id}`}
                  domain={domain}
                  id={id}
                  record={state.data}
                  onRefresh={refresh}
                />
              ) : (
                <DomainActions
                  key={`${id}:${state.data.revision || tick}`}
                  domain={domain}
                  record={state.data}
                  onRefresh={refresh}
                />
              )}
            </>
          )}
        </Loading>
      </>
    ) : null;
  const records = (
    <>
      {domain === "settings" ? (
        <>
          <p className="aw-notice">
            Review existing settings before creating a missing setting. No
            credentials or deploy actions are available here.
          </p>
          {hasCapability(profile, "company.manage") && <OwnerSettings />}
          {hasCapability(profile, "payments.status.read") && <StripeConnection canManage={hasCapability(profile, "payments.manage")} />}
          {hasCapability(profile, "commerce.prices.manage") && <PaperworkPrices />}
          {hasCapability(profile, "commerce.bank.manage") && <BankInstructions />}
          {hasCapability(profile, "legal.manage") && <LegalTexts />}
          <Collection domain={domain} refresh={tick} />
          {/* "general" (company name, support email) is replaced by the owner's Company profile above (L1). */}
          {["resources", "retention"].map((key) => (
            <MissingSetting key={key} id={key} refresh={refresh} tick={tick} />
          ))}
        </>
      ) : (
        <>
          <Collection
            domain={domain}
            refresh={split ? listTick : tick}
            selectedId={split ? id : undefined}
            split={split}
            keep={split}
            extra={
              <div className="aw-row">
                <button className="aw-button" onClick={() => (split ? setListTick((n) => n + 1) : refresh())}>
                  Refresh
                </button>
                {canCreate && (
                  <Link
                    className="aw-button aw-primary"
                    to={`/admin/${domain}/new`}
                  >
                    {domain === "team" || domain === "invitations"
                      ? "Invite staff"
                      : domain === "exports"
                        ? "Create export"
                        : "Create new"}
                  </Link>
                )}
              </div>
            }
          />
          {domain === "media" && hasCapability(profile, "resources.manage") && (
            <>
              <Upload media onDone={refresh} />
              <Collection
                domain="versions"
                params={{ resourceId: "decorative-media" }}
                compact
                refresh={tick}
              />
            </>
          )}
          {domain === "email" && (
            <p className="aw-notice">
              Sent means accepted by the configured email provider. Inbox
              delivery or bounce status is shown only when actually recorded.
            </p>
          )}
        </>
      )}
    </>
  );
  if (!split) return detail || records;
  return (
    <SplitView
      open={Boolean(id)}
      list={records}
      detail={detail}
      detailKey={id}
      title={state.data ? titleOf(state.data) : "Record"}
      onClose={close}
      closeLabel="Close record"
      backLabel="All records"
    />
  );
}
function MissingSetting({ id, refresh, tick }) {
  const state = useRemote(`/admin/settings/${id}`, tick);
  return state.error?.status === 404 ? (
    <SettingsForm id={id} onDone={refresh} />
  ) : null;
}
